import { Component } from './Component';
import { AppStore } from '../store/AppStore';
import { createLogger } from '../utils/logger';

const log = createLogger('UploadScreen');
const MAX_FILE_BYTES = 100 * 1024 * 1024; // 100 MB

interface UploadScreenOptions {
  onFile: (file: File, wikitreeIdOverride?: string) => Promise<void>;
}

export class UploadScreen extends Component {
  private dropZoneEl!: HTMLDivElement;
  private fileInput!: HTMLInputElement;
  private wikitreeInput!: HTMLInputElement;
  private errorEl!: HTMLParagraphElement;
  private loadingEl!: HTMLDivElement;

  // Selector for all natively focusable elements; used by focus trap.
  private static readonly _FOCUSABLE =
    'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  constructor(private opts: UploadScreenOptions) {
    super();
  }

  render(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'upload-screen';
    el.innerHTML = `
      <div class="upload-inner">
        <div class="drop-zone" data-testid="drop-zone"
             tabindex="0"
             aria-label="Drop GEDCOM file here or press Enter to choose a file">
          <p>Drop your GEDCOM file here</p>
          <button type="button" data-testid="file-pick-btn">Choose file</button>
          <input type="file" accept=".ged,.gedcom" style="display:none" aria-hidden="true">
        </div>
        <label class="wikitree-label">
          WikiTree ID (optional)
          <input type="text" data-testid="wikitree-id-input" placeholder="Smith-123">
        </label>
        <p class="privacy-note" data-testid="privacy-note">
          Your GEDCOM never leaves your browser. Parsing, graph building, and rendering all happen
          locally. The only outbound requests are anonymized place-name lookups
          (e.g., "Somerset, PA, USA") to OpenStreetMap's Nominatim service — no names, no dates,
          no identifiers.
        </p>
        <p class="upload-error" data-testid="upload-error" style="display:none"></p>
        <div class="upload-loading" data-testid="upload-loading" style="display:none">
          Parsing&hellip;
        </div>
      </div>
    `;
    this.dropZoneEl = el.querySelector('.drop-zone')!;
    this.fileInput = el.querySelector('input[type="file"]')!;
    this.wikitreeInput = el.querySelector('[data-testid="wikitree-id-input"]')!;
    this.errorEl = el.querySelector('[data-testid="upload-error"]')!;
    this.loadingEl = el.querySelector('[data-testid="upload-loading"]')!;
    return el;
  }

  mount(parent: HTMLElement): void {
    super.mount(parent);
    this._bindEvents();
    // Hide as soon as the first render is delivered (graph non-null) rather than waiting
    // for geocoding to complete. Geocoding continues in the background after the screen
    // is hidden. This prevents a 15 s e2e timeout when the fixture has uncached locations.
    this.track(
      AppStore.subscribe((state) => {
        if (state.graph !== null) {
          this._hide();
        }
      }),
    );
    // Move focus into the dialog so keyboard users can act immediately.
    // Reliable when the page is reached via keyboard (user-activation context exists).
    const pickBtn = this.el.querySelector<HTMLButtonElement>('[data-testid="file-pick-btn"]');
    pickBtn?.focus();
    log.info('UploadScreen mounted');
  }

  private _bindEvents(): void {
    const btn = this.el.querySelector('[data-testid="file-pick-btn"]')!;
    btn.addEventListener('click', () => this.fileInput.click());
    this.fileInput.addEventListener('change', () => {
      const file = this.fileInput.files?.[0];
      if (file) void this._accept(file);
    });
    // Drop-zone keyboard activation: Enter or Space triggers the file picker.
    this.dropZoneEl.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.fileInput.click();
      }
    });
    // Focus trap: keep Tab and Shift+Tab cycling within the dialog.
    this.el.addEventListener('keydown', (e: KeyboardEvent) => this._trapFocus(e));
    this.dropZoneEl.addEventListener('dragover', (e) => {
      e.preventDefault();
      this.dropZoneEl.classList.add('drag-over');
    });
    this.dropZoneEl.addEventListener('dragleave', () => {
      this.dropZoneEl.classList.remove('drag-over');
    });
    this.dropZoneEl.addEventListener('drop', (e) => {
      e.preventDefault();
      this.dropZoneEl.classList.remove('drag-over');
      const file = (e as DragEvent).dataTransfer?.files[0];
      if (file) void this._accept(file);
    });
  }

  private async _accept(file: File): Promise<void> {
    // Reset error state
    this.errorEl.style.display = 'none';
    this.errorEl.textContent = '';

    if (file.size > MAX_FILE_BYTES) {
      this._showError('File too large (max 100 MB).');
      return;
    }

    // Transition: idle → parsing
    this.dropZoneEl.style.display = 'none';
    this.loadingEl.style.display = '';

    const override = this.wikitreeInput.value.trim() || undefined;

    try {
      await this.opts.onFile(file, override);
      // Belt-and-suspenders: hide in case AppStore subscription fired before graph was
      // set (e.g. unit-test environments where AppStore is never updated with a graph).
      this._hide();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this._showError(`Error: ${msg}`);
      // Transition: error → idle
      this.loadingEl.style.display = 'none';
      this.dropZoneEl.style.display = '';
    }
  }

  /** Idempotent: adds .hidden and returns focus to the play/pause button. */
  private _hide(): void {
    if (this.el.classList.contains('hidden')) return;
    this.el.classList.add('hidden');
    // Return focus to the play/pause button so keyboard users can interact immediately.
    const playBtn = document.querySelector<HTMLButtonElement>('[data-testid="play-pause-btn"]');
    playBtn?.focus();
    log.info('UploadScreen hidden — pipeline delivered initial state');
  }

  private _trapFocus(e: KeyboardEvent): void {
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      this.el.querySelectorAll<HTMLElement>(UploadScreen._FOCUSABLE),
    ).filter((el) => !el.closest('[style*="display:none"]'));
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  private _showError(msg: string): void {
    this.errorEl.textContent = msg;
    this.errorEl.style.display = '';
    log.error('UploadScreen error', msg);
  }
}
